import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { ApiException } from '@/lib/api/types';
import * as stockApi from './stockApi';
import type {
  AlertDirection,
  LeadingSectorRef,
  LeadingStock,
  DailyPriceSlice,
  LadderGuide,
  Candle,
  CandleInterval,
  ExchangeRateInfo,
  Price,
  MyTradeSummary,
  EntryIndicators,
  UsTaxEstimate,
  PriceAlert,
  SectorStock,
  SectorSummary,
  StockDetail,
  StockSummary,
  WatchlistCategory,
  WatchlistItem,
} from './stockTypes';
import { MIN_SEARCH_LENGTH } from './stockTypes';

/**
 * 종목 검색 · 관심종목 · 차트 · 시세 훅. (16단계)
 *
 * 갱신 주기가 데이터마다 다르다. 추천(useTrading)이 전부 5분인 것과 대조된다.
 *
 *   검색      캐시 5분. 종목 마스터는 하루 한 번 바뀐다
 *   상세·차트 캐시 5분. 국내 일봉은 15:40(잠정)·20:25(확정), 미국 일봉은 10:30 에 갱신된다
 *   시세      15초마다 다시 읽는다. 장중에만
 *   관심종목  변경할 때만 다시 읽는다
 */

/** 종목 마스터와 일봉은 하루 한 번 바뀐다. 자주 읽어봐야 같은 값이다. */
const STALE_TIME = 5 * 60 * 1000;

/**
 * 시세 폴링 주기.
 *
 * 백엔드 배치가 15초마다 Redis 를 채우므로(2026-09-16) 화면도 15초로 맞춘다.
 * 🔴 배치 주기와 함께 바꾼다 - 화면만 더 짧게 잡으면 같은 캐시 값을 두 번 읽을 뿐이다.
 */
const PRICE_POLL_MS = 15 * 1000;

/**
 * 검색어 입력을 늦추는 시간.
 *
 * 없으면 한 글자마다 요청이 나간다. "삼성전자" 를 치면 4번이다.
 * 한글은 조합 중에도 입력 이벤트가 발생해 실제로는 그보다 많다.
 */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * 입력값을 늦춰 돌려준다.
 *
 * 여기 두는 이유
 *   지금 쓰는 곳이 검색창 하나뿐이다. 두 번째 사용처가 생기면 lib/hooks 로 옮긴다.
 *   (CLAUDE.md 5.2절 — 2회 이상 등장하면 공통화한다)
 */
export function useDebounced<T>(value: T, delayMs = SEARCH_DEBOUNCE_MS): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);

  return debounced;
}

/**
 * 종목 검색.
 *
 * 두 글자 미만이면 요청하지 않는다. 사용자가 두 글자를 치는 도중에
 * 한 글자 상태가 반드시 지나가고, 그때마다 왕복할 이유가 없다.
 */
export function useStockSearch(query: string, country: 'KR' | 'US' = 'KR') {
  const trimmed = query.trim();

  return useQuery<StockSummary[], ApiException>({
    // country 가 키에 들어간다 - 같은 검색어라도 국내/해외 결과가 다르다 (2026-08-20)
    queryKey: ['trading', 'stocks', 'search', country, trimmed],
    queryFn: () => stockApi.searchStocks(trimmed, country),
    enabled: trimmed.length >= MIN_SEARCH_LENGTH,
    staleTime: STALE_TIME,
    // 다음 검색어의 결과가 올 때까지 이전 결과를 남긴다.
    // 없으면 한 글자 칠 때마다 드롭다운이 비었다가 다시 찬다.
    placeholderData: (previous) => previous,
  });
}

/**
 * 종목 상세.
 *
 * 🔴 유니버스 밖 종목은 서버가 지표를 즉석 계산해 수백 ms 느리다.
 *   호출부가 isLoading 을 반드시 표시해야 한다.
 */
export function useStockDetail(stockId: number | null) {
  return useQuery<StockDetail, ApiException>({
    queryKey: ['trading', 'stock', stockId],
    queryFn: () => stockApi.fetchStockDetail(stockId as number),
    enabled: stockId !== null,
    staleTime: STALE_TIME,
  });
}

/**
 * 차트 봉 데이터.
 *
 * 분봉은 캐시를 짧게 잡는다. 서버 Redis TTL 이 장중 60초라
 * 프론트에서 5분을 잡으면 서버가 새 값을 갖고 있어도 화면이 갱신되지 않는다.
 */
export function useCandles(
  stockId: number | null,
  interval: CandleInterval,
  count: number,
) {
  return useQuery<Candle[], ApiException>({
    queryKey: ['trading', 'candles', stockId, interval, count],
    queryFn: () => stockApi.fetchCandles(stockId as number, interval, count),
    enabled: stockId !== null,
    staleTime: interval === '1m' ? 60 * 1000 : STALE_TIME,
  });
}

/**
 * 실시간 시세. 15초마다 다시 읽는다.
 *
 * 🔴 응답의 stale 이 true 면 장이 닫힌 것이다
 *   그래도 폴링은 계속한다. 멈추면 개장 시각에 사용자가 새로고침해야 한다.
 *   15초에 한 번 캐시를 읽는 요청이라 부담이 없다.
 *
 * 탭이 뒤에 있으면 멈춘다. TanStack Query 의 기본 동작이며,
 * 폰에서 앱을 전환해 둔 채 배터리를 쓰는 것을 막는다.
 */
export function usePrices(
  stockIds: number[],
  /**
   * 종목 묶음이 바뀌어도 새 값이 올 때까지 직전 시세를 들고 있는다. (2026-09-28, I-83 ③)
   *
   * 키가 종목 id 목록이라, 가격 알림 탭에서 한 종목의 마지막 알림을 지우면 키가 바뀌어
   * 시세가 잠깐 통째로 비었다 - 그 사이 현재가가 '-' 로 깜빡였다.
   *
   * 🔴 기본값으로 켜지 않는다 - 종목 상세 모달은 `prices.data[0]` 을 읽고, 관심종목은
   *   `data[0].stale` 로 장 상태를 본다. 직전 목록을 들고 있으면 다른 종목을 연 순간
   *   앞 종목의 시세·장 상태가 잠깐 보인다. stockId 로 찾아 쓰는 화면만 켠다.
   */
  options?: { keepPrevious?: boolean },
) {
  const key = stockIds.join(',');

  return useQuery<Price[], ApiException>({
    queryKey: ['trading', 'prices', key],
    queryFn: () => stockApi.fetchPrices(stockIds),
    enabled: stockIds.length > 0,
    refetchInterval: PRICE_POLL_MS,
    refetchIntervalInBackground: false,
    // 시세는 캐시 의미가 없다. 항상 새로 읽는다.
    staleTime: 0,
    placeholderData: options?.keepPrevious ? keepPreviousData : undefined,
  });
}

export function useWatchlist() {
  return useQuery<WatchlistItem[], ApiException>({
    queryKey: ['trading', 'watchlist'],
    queryFn: stockApi.fetchWatchlist,
    // 내가 바꿀 때만 바뀐다. 다른 기기에서 담아도 새로고침하면 보인다.
    staleTime: STALE_TIME,
  });
}

/**
 * 관심종목 담기·빼기·메모·순서.
 *
 * 성공하면 관심종목 목록과 종목 상세를 함께 무효화한다.
 * 상세의 watched 플래그가 별 아이콘 상태를 결정하므로,
 * 목록만 갱신하면 모달의 별이 옛 상태로 남는다.
 */
export function useWatchlistMutations() {
  const queryClient = useQueryClient();

  const invalidate = (stockId?: number) => {
    void queryClient.invalidateQueries({ queryKey: ['trading', 'watchlist'] });
    if (stockId !== undefined) {
      void queryClient.invalidateQueries({ queryKey: ['trading', 'stock', stockId] });
    }
  };

  const add = useMutation<WatchlistItem, ApiException, { stockId: number; memo?: string }>({
    mutationFn: ({ stockId, memo }) => stockApi.addToWatchlist(stockId, memo),
    onSuccess: (_data, variables) => invalidate(variables.stockId),
  });

  const remove = useMutation<boolean, ApiException, number>({
    mutationFn: (stockId) => stockApi.removeFromWatchlist(stockId),
    onSuccess: (_data, stockId) => invalidate(stockId),
  });

  const updateMemo = useMutation<
    WatchlistItem,
    ApiException,
    { stockId: number; memo: string }
  >({
    mutationFn: ({ stockId, memo }) => stockApi.updateWatchlistMemo(stockId, memo),
    onSuccess: (_data, variables) => invalidate(variables.stockId),
  });

  const reorder = useMutation<WatchlistItem[], ApiException, number[]>({
    mutationFn: (stockIds) => stockApi.reorderWatchlist(stockIds),
    // 응답이 갱신된 목록 전체라 그대로 캐시에 넣는다. 다시 읽을 필요가 없다.
    onSuccess: (items) => queryClient.setQueryData(['trading', 'watchlist'], items),
  });

  return { add, remove, updateMemo, reorder };
}

/**
 * 관심종목 카테고리 목록. (3.10)
 *
 * 관심종목과 따로 읽는다. 한 응답에 묶지 않는 이유
 *   카테고리는 거의 바뀌지 않고 관심종목은 담을 때마다 바뀐다.
 *   묶어 두면 종목 하나 담을 때마다 카테고리까지 다시 받는다.
 *   두 질의는 병렬로 나가므로 왕복이 늘어나는 것도 아니다.
 */
export function useWatchlistCategories() {
  return useQuery<WatchlistCategory[], ApiException>({
    queryKey: ['trading', 'watchlist', 'categories'],
    queryFn: stockApi.fetchWatchlistCategories,
    staleTime: STALE_TIME,
  });
}

/**
 * 카테고리 만들기·이름 변경·삭제. (3.10)
 *
 * 🔴 삭제는 관심종목 목록도 함께 무효화한다
 *   그 안의 종목이 미분류로 바뀌기 때문이다. 카테고리만 다시 읽으면
 *   목록의 categoryId 가 사라진 id 를 계속 가리켜 어느 탭에도 안 보인다.
 */
export function useWatchlistCategoryMutations() {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    // ['trading','watchlist'] 는 카테고리 키의 접두사이기도 해서 둘 다 무효화된다.
    void queryClient.invalidateQueries({ queryKey: ['trading', 'watchlist'] });
  };

  const create = useMutation<WatchlistCategory, ApiException, string>({
    mutationFn: (name) => stockApi.createWatchlistCategory(name),
    onSuccess: invalidateAll,
  });

  const rename = useMutation<
    WatchlistCategory,
    ApiException,
    { categoryId: number; name: string }
  >({
    mutationFn: ({ categoryId, name }) =>
      stockApi.renameWatchlistCategory(categoryId, name),
    onSuccess: invalidateAll,
  });

  /** 응답은 소속이 풀린 관심종목 수다. 관심종목 자체는 지워지지 않는다. */
  const remove = useMutation<number, ApiException, number>({
    mutationFn: (categoryId) => stockApi.deleteWatchlistCategory(categoryId),
    onSuccess: invalidateAll,
  });

  return { create, rename, remove };
}

/**
 * 카테고리에 종목 담기·빼기·구성 확정. (3.10)
 *
 * 카테고리 자체의 CRUD(useWatchlistCategoryMutations)와 가른 이유
 *   부르는 화면이 다르다. 이쪽은 종목 상세 모달의 체크박스와 목록의 편집 모드가 쓰고,
 *   저쪽은 관리 모달만 쓴다. 한 훅에 담으면 어느 화면이든 다섯 개를 다 받게 된다.
 *
 * 관심종목 목록만 무효화한다. 카테고리 이름은 바뀌지 않는다.
 */
export function useWatchlistCategoryItemMutations() {
  const queryClient = useQueryClient();

  const invalidateList = () => {
    void queryClient.invalidateQueries({ queryKey: ['trading', 'watchlist'] });
  };

  const addItem = useMutation<
    void,
    ApiException,
    { categoryId: number; stockId: number }
  >({
    mutationFn: ({ categoryId, stockId }) =>
      stockApi.addWatchlistCategoryItem(categoryId, stockId),
    onSuccess: invalidateList,
  });

  const removeItem = useMutation<
    boolean,
    ApiException,
    { categoryId: number; stockId: number }
  >({
    mutationFn: ({ categoryId, stockId }) =>
      stockApi.removeWatchlistCategoryItem(categoryId, stockId),
    onSuccess: invalidateList,
  });

  /** 편집 모드의 저장. 순서 변경과 빼기가 한 요청이다 */
  const replaceItems = useMutation<
    void,
    ApiException,
    { categoryId: number; stockIds: number[] }
  >({
    mutationFn: ({ categoryId, stockIds }) =>
      stockApi.replaceWatchlistCategoryItems(categoryId, stockIds),
    onSuccess: invalidateList,
  });

  return { addItem, removeItem, replaceItems };
}

/**
 * 저점매수 가격 알림 목록. (2026-08-20)
 *
 * 15초마다 다시 읽는다. 발동은 서버에서 일어나고(장중 매분 검사)
 * 화면은 그 결과를 따라가기만 한다 - 발동한 알림이 "꺼짐 + 발동가" 로 바뀐다.
 */
export function usePriceAlerts() {
  return useQuery<PriceAlert[], ApiException>({
    queryKey: ['trading', 'price-alerts'],
    queryFn: stockApi.fetchPriceAlerts,
    refetchInterval: PRICE_POLL_MS,
  });
}

/**
 * 매매일지. (13단계)
 *
 * 🔴 2026-09-20 (I-76) — "평일 16:00 하루 한 번" 이라는 전제가 깨졌다
 *   계좌 동기화가 셋으로 늘었다(화면 볼 때 60초 · 주문 직후 · 확정 스냅샷 셋).
 *   다만 이 훅은 계좌 화면이 아니라 매매일지라, 60초 폴링까지 붙일 필요는 없다고 봤다 —
 *   일지는 "오늘 몇 초 사이에 체결됐나" 를 보는 화면이 아니다.
 *   장중에 방금 체결을 보려면 [계좌] 화면이 그 일을 한다(useAccount 의 REFRESH_MS).
 */
export function useMyTrades(days: number) {
  return useQuery<MyTradeSummary, ApiException>({
    queryKey: ['trading', 'my-trades', days],
    queryFn: () => stockApi.fetchMyTrades(days),
    staleTime: STALE_TIME,
  });
}

/** 미국 주식 양도세 어림 (2026-10-16, docs/impl/150 9번). 일지와 같은 주기 */
export function useUsTaxEstimate() {
  return useQuery<UsTaxEstimate, ApiException>({
    queryKey: ['trading', 'my-trades', 'us-tax'],
    queryFn: stockApi.fetchUsTaxEstimate,
    staleTime: STALE_TIME,
  });
}

/** 매매일지 — 산 날 시스템 지표 (2026-10-16, docs/impl/150 8번). 일지와 같은 주기 */
export function useEntryIndicators(days: number) {
  return useQuery<EntryIndicators, ApiException>({
    queryKey: ['trading', 'my-trades', 'entry-indicators', days],
    queryFn: () => stockApi.fetchEntryIndicators(days),
    staleTime: STALE_TIME,
  });
}

/**
 * 섹터 목록. 분류는 마이그레이션이 정본이라 거의 바뀌지 않는다. (2026-08-23)
 */
/**
 * 주도 섹터 목록. (V128, docs/impl/113)
 *
 * 관리자가 바꾸기 전에는 값이 그대로라 캐시를 길게 둔다.
 */
export function useLeadingSectors() {
  return useQuery<LeadingSectorRef[], ApiException>({
    queryKey: ['trading', 'leading', 'sectors'],
    queryFn: stockApi.fetchLeadingSectors,
    staleTime: STALE_TIME,
  });
}

/**
 * 주도주 종목. 거래대금 상위 LEADING_STOCK_LIMIT(✏️ 10/9 50 → 100).
 *
 * 거래대금은 일봉이 갱신될 때만 바뀌므로(코스피 15:40 · 20:25 확정 · 코스닥 20:30)
 * 섹터 화면과 같은 캐시를 쓴다.
 */
export function useLeadingStocks(limit = stockApi.LEADING_STOCK_LIMIT) {
  return useQuery<LeadingStock[], ApiException>({
    queryKey: ['trading', 'leading', 'stocks', limit],
    queryFn: () => stockApi.fetchLeadingStocks(limit),
    staleTime: STALE_TIME,
  });
}

export function useSectors() {
  return useQuery<SectorSummary[], ApiException>({
    queryKey: ['trading', 'sectors'],
    queryFn: stockApi.fetchSectors,
    staleTime: STALE_TIME,
  });
}

/** 한 섹터의 종목. 시가총액은 하루 한 번(평일 21:15) 갱신되므로 캐시를 길게 둔다. */
export function useSectorStocks(code: string | null) {
  return useQuery<SectorStock[], ApiException>({
    queryKey: ['trading', 'sectors', code],
    queryFn: () => stockApi.fetchSectorStocks(code as string),
    enabled: code !== null,
    staleTime: STALE_TIME,
  });
}

/**
 * 최신 USD → KRW 환율. (2026-08-20)
 *
 * 서버가 매시 정각에만 갱신하므로 자주 읽을 이유가 없다.
 * enabled 로 해외 탭·미국 종목을 볼 때만 요청한다.
 *
 * 🔴 아직 한 줄도 없으면(배포 직후) data 가 null 이다. 화면은 병기를 숨긴다.
 */
export function useUsdKrwRate(enabled: boolean) {
  return useQuery<ExchangeRateInfo | null, ApiException>({
    queryKey: ['trading', 'exchange-rate'],
    queryFn: stockApi.fetchExchangeRate,
    enabled,
    staleTime: STALE_TIME,
  });
}

/**
 * 지금 환율 - 환율 탭을 열어 둔 동안 1분마다 다시 읽는다. (2026-09-19, I-61)
 *
 * 1분인 이유 - 토스 환율이 1분마다 바뀌고(스펙) 서버도 60초 캐시한다.
 * 더 자주 읽어도 같은 값이 온다.
 */
export function useLiveUsdKrwRate() {
  return useQuery<ExchangeRateInfo | null, ApiException>({
    queryKey: ['trading', 'exchange-rate', 'live'],
    queryFn: stockApi.fetchLiveExchangeRate,
    refetchInterval: 60 * 1000,
    staleTime: 60 * 1000,
  });
}

/** 최근 며칠의 매시 환율. 이력은 매시 정각에만 늘어서 5분 캐시로 충분하다 (I-61) */
export function useUsdKrwHistory(days: number) {
  return useQuery<ExchangeRateInfo[], ApiException>({
    queryKey: ['trading', 'exchange-rate', 'history', days],
    queryFn: () => stockApi.fetchExchangeRateHistory(days),
    staleTime: STALE_TIME,
  });
}

/** 알림 걸기(재무장 포함)와 지우기. */
export function usePriceAlertMutations() {
  const queryClient = useQueryClient();

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['trading', 'price-alerts'] });
  };

  const set = useMutation<
    PriceAlert,
    ApiException,
    { stockId: number; targetPrice: number; direction?: AlertDirection }
  >({
    mutationFn: ({ stockId, targetPrice, direction }) =>
      stockApi.setPriceAlert(stockId, targetPrice, direction ?? 'BELOW'),
    onSuccess: invalidate,
  });

  const remove = useMutation<
    boolean,
    ApiException,
    { stockId: number; direction?: AlertDirection }
  >({
    mutationFn: ({ stockId, direction }) => stockApi.removePriceAlert(stockId, direction),
    onSuccess: invalidate,
  });

  /**
   * 알림 한 건만 지운다. (2026-09-02, V123)
   *
   * 한 종목에 최대 5개가 걸리면서 "종목 + 방향" 으로는 하나를 특정할 수 없게 됐다.
   * 화면의 삭제 버튼은 전부 이것을 쓴다. remove 는 관심종목에서 뺄 때만 남는다.
   */
  const removeOne = useMutation<boolean, ApiException, { alertId: number }>({
    mutationFn: ({ alertId }) => stockApi.removePriceAlertById(alertId),
    onSuccess: invalidate,
  });

  /**
   * 울려서 꺼진 알림을 다시 켠다. (2026-09-03 사용자 요청)
   *
   * 기준가를 옮겨 적을 필요가 없다 - 전에는 종목 모달을 열어 같은 값으로
   * 다시 저장해야 했다.
   */
  const rearm = useMutation<PriceAlert, ApiException, { alertId: number }>({
    mutationFn: ({ alertId }) => stockApi.rearmPriceAlert(alertId),
    onSuccess: invalidate,
  });

  /**
   * 걸어둔 알림의 기준가를 그 자리에서 고친다. (2026-09-28 사용자 요청, I-83 ②)
   *
   * 전에는 지우고 종목 모달에서 다시 거는 수밖에 없었다. 고치면 다시 켜진다.
   */
  const changePrice = useMutation<
    PriceAlert,
    ApiException,
    { alertId: number; targetPrice: number }
  >({
    mutationFn: ({ alertId, targetPrice }) =>
      stockApi.changePriceAlertPrice(alertId, targetPrice),
    onSuccess: invalidate,
  });

  /**
   * 울린 알림을 한꺼번에 다시 켜거나 지운다. (2026-10-09 사용자 요청)
   *
   * 서버에 일괄 API 를 따로 두지 않고 한 건짜리(rearm · removeOne 과 같은 API)를 여러 번 부른다 —
   * 한 사람이 건 알림이라 많아야 수십 건이고, 권한 검사 · 기록 로그가 한 건짜리와 똑같이 남는다.
   * 하나가 실패해도 나머지는 계속한다. 실패한 건수를 돌려주면 화면이 알린다.
   */
  const rearmMany = useMutation<number, ApiException, { alertIds: number[] }>({
    mutationFn: ({ alertIds }) => countFailures(alertIds.map((id) => stockApi.rearmPriceAlert(id))),
    onSettled: invalidate,
  });
  const removeMany = useMutation<number, ApiException, { alertIds: number[] }>({
    mutationFn: ({ alertIds }) =>
      countFailures(alertIds.map((id) => stockApi.removePriceAlertById(id))),
    onSettled: invalidate,
  });

  return { set, remove, removeOne, rearm, changePrice, rearmMany, removeMany };
}

/** 여러 요청을 끝까지 기다리고 실패한 수를 센다 — 하나가 실패해도 나머지를 멈추지 않는다 */
async function countFailures<T>(requests: Promise<T>[]): Promise<number> {
  const results = await Promise.allSettled(requests);
  return results.filter((result) => result.status === 'rejected').length;
}

// ── 일자별 시세 (2026-08-28) ──

/**
 * 증권사 앱의 "일자별" 표 - 무한 스크롤.
 *
 * 커서가 오프셋이 아니라 날짜다. 밤새 일봉이 추가돼도 같은 줄이 두 번
 * 보이지 않는다. 🔴 최대 1년 - 경계는 서버가 자른다.
 *
 * 폴링하지 않는다. 일봉은 하루에 한 번(15:40 배치) 바뀌는 데이터다.
 */
export function useLadderGuide(stockId: number | null) {
  // 일봉으로 계산한 값이라 하루 한 번 바뀐다 - 폴링하지 않는다(useDailyPrices 와 같은 사정)
  return useQuery<LadderGuide, ApiException>({
    queryKey: ['trading', 'ladder-guide', stockId],
    queryFn: () => stockApi.fetchLadderGuide(stockId as number),
    enabled: stockId !== null,
    staleTime: 5 * 60 * 1000,
  });
}

export function useDailyPrices(stockId: number | null) {
  return useInfiniteQuery<DailyPriceSlice, ApiException>({
    queryKey: ['trading', 'daily-prices', stockId],
    queryFn: ({ pageParam }) =>
      stockApi.fetchDailyPrices(stockId as number, pageParam as string | undefined),
    initialPageParam: undefined,
    getNextPageParam: (last) => (last.hasMore ? (last.nextBefore ?? undefined) : undefined),
    enabled: stockId !== null,
    staleTime: 5 * 60 * 1000,
  });
}
